import { useMemo, useEffect, useState } from 'react';

/**
 * ============================================================================
 * AR VIEWER URL PARAMETERS - DOCUMENTATION
 * ============================================================================
 *
 * Base URL: /product/public/ar-viewer/:id
 * Where :id is the product's unique identifier (UUID)
 *
 * ----------------------------------------------------------------------------
 * AVAILABLE PARAMETERS
 * ----------------------------------------------------------------------------
 *
 * CONTENT PARAMETERS:
 * | Parameter | Description                    | Example                    |
 * |-----------|--------------------------------|----------------------------|
 * | title     | Custom title text              | title=View+in+AR           |
 * | desc      | Custom description text        | desc=Scan+to+view          |
 * | btnText   | Custom button text             | btnText=Open+AR            |
 * | lang      | Language code for translations | lang=fr                    |
 *
 * NOTE: The `lang` parameter only affects translations when `title`, `desc`,
 * and `btnText` are NOT provided. If you provide custom text via URL, the
 * `lang` parameter will have no effect on those texts.
 *
 * FONT PARAMETER:
 * | Parameter | Description                              | Example                |
 * |-----------|------------------------------------------|------------------------|
 * | font      | Font family (applies to all elements)    | font=Jost,sans-serif   |
 *
 * NOTE:
 * - System fonts (Arial, Helvetica, Georgia, etc.) are applied directly.
 * - Google Fonts are auto-loaded. If a Google Font doesn't exist,
 *   the default application font will be used.
 *
 * TITLE STYLING:
 * | Parameter   | Description       | Example          |
 * |-------------|-------------------|------------------|
 * | titleColor  | Title text color  | titleColor=ff0000|
 * | titleSize   | Title font size   | titleSize=28px   |
 * | titleWeight | Title font weight | titleWeight=700  |
 *
 * DESCRIPTION STYLING:
 * | Parameter  | Description             | Example          |
 * |------------|-------------------------|------------------|
 * | descColor  | Description text color  | descColor=666666 |
 * | descSize   | Description font size   | descSize=16px    |
 * | descWeight | Description font weight | descWeight=400   |
 *
 * BUTTON STYLING:
 * | Parameter   | Description                    | Example              |
 * |-------------|--------------------------------|----------------------|
 * | btnTextColor | Button text color              | btnTextColor=ffffff   |
 * | btnBgColor  | Button background/border color | btnBgColor=f5a623    |
 * | btnSize     | Button font size               | btnSize=16px         |
 * | btnWeight   | Button font weight             | btnWeight=600        |
 * | btnPadding  | Button padding                 | btnPadding=12px+24px |
 * | btnRadius   | Button border radius           | btnRadius=8px        |
 *
 * ----------------------------------------------------------------------------
 * ACCEPTED VALUES
 * ----------------------------------------------------------------------------
 *
 * COLORS:
 * - Hex without #: ff0000, 000, ffffff
 * - Hex with #: #ff0000 (note: # becomes %23 in URL)
 * - RGB: rgb(255,0,0)
 * - RGBA: rgba(255,0,0,0.5)
 * - HSL: hsl(0,100%,50%)
 * - Named colors: red, blue, transparent, etc.
 *
 * FONT SIZES:
 * - Pixels: 16px, 24px, 32px
 * - REM: 1rem, 1.5rem, 2rem
 * - EM: 1em, 1.5em, 2em
 *
 * FONT WEIGHTS:
 * - Numeric: 100, 200, 300, 400, 500, 600, 700, 800, 900
 * - Named: normal, bold, lighter, bolder
 *
 * PADDING:
 * - Single value: 10px, 1rem
 * - Two values (vertical horizontal): 10px+20px, 1rem+2rem
 * - Four values (top right bottom left): 10px+20px+10px+20px
 *
 * BORDER RADIUS:
 * - Pixels: 4px, 8px, 20px
 * - REM/EM: 0.5rem, 1em
 * - Percentage: 50%, 100%
 * - Plain number (treated as pixels): 20 → 20px
 *
 * ----------------------------------------------------------------------------
 * URL ENCODING
 * ----------------------------------------------------------------------------
 * | Character | URL Encoded |
 * |-----------|-------------|
 * | Space     | + or %20    |
 * | #         | %23         |
 * | ,         | %2C         |
 *
 * ----------------------------------------------------------------------------
 * EXAMPLES
 * ----------------------------------------------------------------------------
 *
 * 1. Basic Custom Text:
 *    ?title=View+Product+in+AR&desc=Scan+the+QR+code&btnText=Launch+AR
 *
 * 2. French Translation (using lang):
 *    ?lang=fr
 *
 * 3. Custom Styling with Google Font:
 *    ?font=Jost,sans-serif&titleColor=ff0000&titleSize=28px&titleWeight=700
 *
 * 4. Full Customization:
 *    ?font=Jost,sans-serif&title=Experience+in+AR&titleColor=ff0000&titleSize=28px
 *    &titleWeight=700&desc=Scan+to+view&descColor=333333&descSize=18px
 *    &btnText=Open+AR&btnTextColor=ffffff&btnBgColor=f5a623&btnPadding=12px+24px
 *    &btnRadius=8px
 *
 * 5. Styling Only (Keep Default Translated Text):
 *    ?lang=fr&font=Roboto&titleColor=0066cc&btnBgColor=0066cc&btnRadius=50px
 *
 * ----------------------------------------------------------------------------
 * COMPLETE PARAMETER REFERENCE
 * ----------------------------------------------------------------------------
 * | Parameter   | Type    | Description                                      |
 * |-------------|---------|--------------------------------------------------|
 * | lang        | string  | Language code (e.g., en, fr). Only works when    |
 * |             |         | custom text is NOT provided.                     |
 * | font        | string  | Google Font family (e.g., Jost,sans-serif)       |
 * | title       | string  | Custom title text (overrides translation)        |
 * | titleColor  | color   | Title text color                                 |
 * | titleSize   | size    | Title font size                                  |
 * | titleWeight | weight  | Title font weight                                |
 * | desc        | string  | Custom description text (overrides translation)  |
 * | descColor   | color   | Description text color                           |
 * | descSize    | size    | Description font size                            |
 * | descWeight  | weight  | Description font weight                          |
 * | btnText     | string  | Custom button text (overrides translation)       |
 * | btnTextColor | color   | Button text color                                |
 * | btnBgColor  | color   | Button background and border color               |
 * | btnSize     | size    | Button font size                                 |
 * | btnWeight   | weight  | Button font weight                               |
 * | btnPadding  | padding | Button padding                                   |
 * | btnRadius   | radius  | Button border radius                             |
 *
 * ============================================================================
 * 360 VIEWER URL PARAMETERS
 * ============================================================================
 *
 * Base URL: /product/public/360-viewer/:id
 * Where :id is the product's unique identifier (UUID)
 *
 * ----------------------------------------------------------------------------
 * AVAILABLE PARAMETERS
 * ----------------------------------------------------------------------------
 *
 * CONTENT PARAMETERS:
 * | Parameter | Description                    | Example                             |
 * |-----------|--------------------------------|-------------------------------------|
 * | helpText  | Custom help text               | helpText=Click+and+drag+to+rotate   |
 * | lang      | Language code for translations | lang=fr                             |
 *
 * NOTE: The `lang` parameter only affects translations when `helpText` is NOT
 * provided. If you provide custom text via URL, the `lang` parameter will
 * have no effect on that text.
 *
 * FONT PARAMETER:
 * | Parameter | Description                              | Example                |
 * |-----------|------------------------------------------|------------------------|
 * | font      | Font family (applies to text)            | font=Jost,sans-serif   |
 *
 * HELP TEXT STYLING:
 * | Parameter      | Description           | Example              |
 * |----------------|-----------------------|----------------------|
 * | helpTextColor  | Help text color       | helpTextColor=ff0000 |
 * | helpTextSize   | Help text font size   | helpTextSize=16px    |
 * | helpTextWeight | Help text font weight | helpTextWeight=700   |
 *
 * ----------------------------------------------------------------------------
 * 360 VIEWER EXAMPLES
 * ----------------------------------------------------------------------------
 *
 * 1. Basic Custom Text:
 *    ?helpText=Drag+to+rotate+the+product
 *
 * 2. French Translation (using lang):
 *    ?lang=fr
 *
 * 3. Custom Styling with Google Font:
 *    ?font=Jost,sans-serif&helpTextColor=ff0000&helpTextSize=18px&helpTextWeight=700
 *
 * 4. Full Customization:
 *    ?helpText=Rotate+360&font=Roboto&helpTextColor=333333&helpTextSize=16px&helpTextWeight=500
 *
 * 5. Styling Only (Keep Default Translated Text):
 *    ?lang=fr&font=Arial&helpTextColor=0066cc
 *
 * ----------------------------------------------------------------------------
 * 360 VIEWER COMPLETE PARAMETER REFERENCE
 * ----------------------------------------------------------------------------
 * | Parameter      | Type   | Description                                    |
 * |----------------|--------|------------------------------------------------|
 * | lang           | string | Language code (e.g., en, fr). Only works when  |
 * |                |        | custom text is NOT provided.                   |
 * | font           | string | Font family (e.g., Jost,sans-serif)            |
 * | helpText       | string | Custom help text (overrides translation)       |
 * | helpTextColor  | color  | Help text color                                |
 * | helpTextSize   | size   | Help text font size                            |
 * | helpTextWeight | weight | Help text font weight                          |
 *
 * ============================================================================
 */

/**
 * Sanitizes text to prevent XSS attacks
 * Removes HTML tags, script tags, event handlers, and dangerous characters
 * @param {string} text - The text to sanitize
 * @returns {string} - Sanitized text safe for DOM insertion
 */
export const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';

  // Decode URL-encoded characters first
  let decoded = text;
  try {
    decoded = decodeURIComponent(text);
  } catch (e) {
    // If decoding fails, use original text
    decoded = text;
  }

  // Remove HTML tags
  let sanitized = decoded.replace(/<[^>]*>/g, '');

  // Remove script-related content
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/vbscript:/gi, '');
  sanitized = sanitized.replace(/data:/gi, '');

  // Remove event handlers (on* attributes)
  sanitized = sanitized.replace(/on\w+\s*=/gi, '');

  // Remove dangerous characters that could be used for injection
  sanitized = sanitized.replace(/[<>]/g, '');

  // Trim whitespace
  sanitized = sanitized.trim();

  // Limit length to prevent DoS
  const MAX_LENGTH = 500;
  if (sanitized.length > MAX_LENGTH) {
    sanitized = sanitized.substring(0, MAX_LENGTH);
  }

  return sanitized;
};

/**
 * Validates and sanitizes a font family value
 * Only allows alphanumeric characters, spaces, hyphens, underscores, and commas
 * @param {string} fontFamily - The font family value to validate
 * @returns {string|null} - Valid font family or null
 */
const sanitizeFontFamily = (fontFamily) => {
  if (!fontFamily || typeof fontFamily !== 'string') return null;

  let decoded = fontFamily;
  try {
    decoded = decodeURIComponent(fontFamily.replace(/\+/g, ' '));
  } catch (e) {
    decoded = fontFamily.replace(/\+/g, ' ');
  }

  // Trim whitespace
  decoded = decoded.trim();

  // Only allow safe characters: letters, numbers, spaces, hyphens, underscores, commas, and single quotes
  // This prevents CSS injection attacks
  if (!/^[a-zA-Z0-9\s\-_,'"]+$/.test(decoded)) {
    return null;
  }

  // Limit length
  const MAX_LENGTH = 200;
  if (decoded.length > MAX_LENGTH) {
    return null;
  }

  return decoded;
};

/**
 * Validates and sanitizes a CSS color value
 * Accepts: hex (#fff, #ffffff, #ffffffff), rgb, rgba, hsl, hsla, named colors
 * @param {string} color - The color value to validate
 * @returns {string|null} - Valid color or null
 */
const sanitizeColor = (color) => {
  if (!color || typeof color !== 'string') return null;

  let decoded = color;
  try {
    decoded = decodeURIComponent(color);
  } catch (e) {
    decoded = color;
  }

  // Trim whitespace
  decoded = decoded.trim();

  // Allow hex colors with # (#fff, #ffffff, #ffffffff for alpha)
  if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(decoded)) {
    return decoded;
  }

  // Allow hex colors without # prefix - add # automatically
  if (/^([0-9A-Fa-f]{3}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(decoded)) {
    return `#${decoded}`;
  }

  // Allow rgb/rgba colors with various formats
  // rgb(255, 255, 255) or rgb(255 255 255) or rgba(255, 255, 255, 0.5)
  if (
    /^rgba?\(\s*\d+(\.\d+)?(%?)(\s*[,\s]\s*\d+(\.\d+)?(%?)){2}(\s*[,\/\s]\s*(0|1|0?\.\d+|\d+(\.\d+)?%?))?\s*\)$/i.test(
      decoded
    )
  ) {
    return decoded;
  }

  // Allow hsl/hsla colors
  // hsl(360, 100%, 50%) or hsla(360, 100%, 50%, 0.5)
  if (
    /^hsla?\(\s*\d+(\.\d+)?(deg|rad|turn)?(\s*[,\s]\s*\d+(\.\d+)?%){2}(\s*[,\/\s]\s*(0|1|0?\.\d+|\d+(\.\d+)?%?))?\s*\)$/i.test(
      decoded
    )
  ) {
    return decoded;
  }

  // All CSS named colors (full list)
  const namedColors = [
    'aliceblue',
    'antiquewhite',
    'aqua',
    'aquamarine',
    'azure',
    'beige',
    'bisque',
    'black',
    'blanchedalmond',
    'blue',
    'blueviolet',
    'brown',
    'burlywood',
    'cadetblue',
    'chartreuse',
    'chocolate',
    'coral',
    'cornflowerblue',
    'cornsilk',
    'crimson',
    'cyan',
    'darkblue',
    'darkcyan',
    'darkgoldenrod',
    'darkgray',
    'darkgrey',
    'darkgreen',
    'darkkhaki',
    'darkmagenta',
    'darkolivegreen',
    'darkorange',
    'darkorchid',
    'darkred',
    'darksalmon',
    'darkseagreen',
    'darkslateblue',
    'darkslategray',
    'darkslategrey',
    'darkturquoise',
    'darkviolet',
    'deeppink',
    'deepskyblue',
    'dimgray',
    'dimgrey',
    'dodgerblue',
    'firebrick',
    'floralwhite',
    'forestgreen',
    'fuchsia',
    'gainsboro',
    'ghostwhite',
    'gold',
    'goldenrod',
    'gray',
    'grey',
    'green',
    'greenyellow',
    'honeydew',
    'hotpink',
    'indianred',
    'indigo',
    'ivory',
    'khaki',
    'lavender',
    'lavenderblush',
    'lawngreen',
    'lemonchiffon',
    'lightblue',
    'lightcoral',
    'lightcyan',
    'lightgoldenrodyellow',
    'lightgray',
    'lightgrey',
    'lightgreen',
    'lightpink',
    'lightsalmon',
    'lightseagreen',
    'lightskyblue',
    'lightslategray',
    'lightslategrey',
    'lightsteelblue',
    'lightyellow',
    'lime',
    'limegreen',
    'linen',
    'magenta',
    'maroon',
    'mediumaquamarine',
    'mediumblue',
    'mediumorchid',
    'mediumpurple',
    'mediumseagreen',
    'mediumslateblue',
    'mediumspringgreen',
    'mediumturquoise',
    'mediumvioletred',
    'midnightblue',
    'mintcream',
    'mistyrose',
    'moccasin',
    'navajowhite',
    'navy',
    'oldlace',
    'olive',
    'olivedrab',
    'orange',
    'orangered',
    'orchid',
    'palegoldenrod',
    'palegreen',
    'paleturquoise',
    'palevioletred',
    'papayawhip',
    'peachpuff',
    'peru',
    'pink',
    'plum',
    'powderblue',
    'purple',
    'rebeccapurple',
    'red',
    'rosybrown',
    'royalblue',
    'saddlebrown',
    'salmon',
    'sandybrown',
    'seagreen',
    'seashell',
    'sienna',
    'silver',
    'skyblue',
    'slateblue',
    'slategray',
    'slategrey',
    'snow',
    'springgreen',
    'steelblue',
    'tan',
    'teal',
    'thistle',
    'tomato',
    'turquoise',
    'violet',
    'wheat',
    'white',
    'whitesmoke',
    'yellow',
    'yellowgreen',
    'transparent',
    'currentcolor',
    'inherit',
  ];
  if (namedColors.includes(decoded.toLowerCase())) {
    return decoded.toLowerCase();
  }

  return null;
};

/**
 * Validates and sanitizes a font size value
 * @param {string} size - The font size value to validate
 * @returns {string|null} - Valid font size or null
 */
const sanitizeFontSize = (size) => {
  if (!size || typeof size !== 'string') return null;

  let decoded = size;
  try {
    decoded = decodeURIComponent(size);
  } catch (e) {
    decoded = size;
  }

  // Trim whitespace
  decoded = decoded.trim();

  // Allow pixel values (e.g., 16px, 24px, 100px)
  if (/^\d+(\.\d+)?px$/i.test(decoded)) {
    return decoded;
  }

  // Allow rem values (e.g., 1rem, 1.5rem, 10rem)
  if (/^\d+(\.\d+)?rem$/i.test(decoded)) {
    return decoded;
  }

  // Allow em values (e.g., 1em, 2.5em)
  if (/^\d+(\.\d+)?em$/i.test(decoded)) {
    return decoded;
  }

  // Allow plain numbers (default to px)
  if (/^\d+(\.\d+)?$/.test(decoded)) {
    return `${decoded}px`;
  }

  return null;
};

/**
 * Validates and sanitizes a font weight value
 * @param {string} weight - The font weight value to validate
 * @returns {string|null} - Valid font weight or null
 */
const sanitizeFontWeight = (weight) => {
  if (!weight || typeof weight !== 'string') return null;

  let decoded = weight;
  try {
    decoded = decodeURIComponent(weight);
  } catch (e) {
    decoded = weight;
  }

  // Allow numeric weights (100-900)
  if (/^\d+$/.test(decoded)) {
    const numValue = parseInt(decoded, 10);
    if (numValue >= 100 && numValue <= 900 && numValue % 100 === 0) {
      return decoded;
    }
  }

  // Allow named weights
  const namedWeights = ['normal', 'bold', 'lighter', 'bolder'];
  if (namedWeights.includes(decoded.toLowerCase())) {
    return decoded.toLowerCase();
  }

  return null;
};

/**
 * Validates and sanitizes a padding value
 * @param {string} padding - The padding value to validate
 * @returns {string|null} - Valid padding or null
 */
const sanitizePadding = (padding) => {
  if (!padding || typeof padding !== 'string') return null;

  let decoded = padding;
  try {
    // Replace + with space before decoding (+ is used for spaces in query strings)
    decoded = decodeURIComponent(padding.replace(/\+/g, ' '));
  } catch (e) {
    decoded = padding.replace(/\+/g, ' ');
  }

  // Trim and normalize multiple spaces to single space
  decoded = decoded.trim().replace(/\s+/g, ' ');

  // Allow single value (e.g., 10px, 1rem)
  const singleValueRegex = /^\d+(\.\d+)?(px|rem|em)$/i;

  // Allow two values (e.g., 10px 20px)
  const twoValueRegex = /^\d+(\.\d+)?(px|rem|em)\s+\d+(\.\d+)?(px|rem|em)$/i;

  // Allow four values (e.g., 10px 20px 10px 20px)
  const fourValueRegex = /^\d+(\.\d+)?(px|rem|em)\s+\d+(\.\d+)?(px|rem|em)\s+\d+(\.\d+)?(px|rem|em)\s+\d+(\.\d+)?(px|rem|em)$/i;

  if (singleValueRegex.test(decoded) || twoValueRegex.test(decoded) || fourValueRegex.test(decoded)) {
    return decoded;
  }

  return null;
};

/**
 * Validates and sanitizes a border radius value
 * @param {string} radius - The border radius value to validate
 * @returns {string|null} - Valid border radius or null
 */
const sanitizeBorderRadius = (radius) => {
  if (!radius || typeof radius !== 'string') return null;

  let decoded = radius;
  try {
    decoded = decodeURIComponent(radius);
  } catch (e) {
    decoded = radius;
  }

  // Trim whitespace
  decoded = decoded.trim();

  // Allow plain numbers (treat as pixels)
  if (/^\d+(\.\d+)?$/.test(decoded)) {
    return `${parseFloat(decoded)}px`;
  }

  // Allow pixel values (e.g., 4px, 8px, 200px)
  if (/^\d+(\.\d+)?px$/i.test(decoded)) {
    return decoded;
  }

  // Allow rem values (e.g., 0.5rem, 1rem, 20rem)
  if (/^\d+(\.\d+)?rem$/i.test(decoded)) {
    return decoded;
  }

  // Allow em values (e.g., 1em, 5em)
  if (/^\d+(\.\d+)?em$/i.test(decoded)) {
    return decoded;
  }

  // Allow percentage values (e.g., 50%, 100%)
  if (/^\d+(\.\d+)?%$/.test(decoded)) {
    return decoded;
  }

  return null;
};

/**
 * Extracts the primary font name from a font-family string
 * e.g., '"Jost", sans-serif' => 'Jost'
 * @param {string} fontFamily - The font family string
 * @returns {string|null} - Primary font name or null
 */
const extractPrimaryFont = (fontFamily) => {
  if (!fontFamily) return null;

  // Split by comma and get the first font
  const fonts = fontFamily.split(',');
  if (fonts.length === 0) return null;

  // Remove quotes and trim
  let primaryFont = fonts[0].trim().replace(/["']/g, '');

  // Skip generic font families
  const genericFonts = ['serif', 'sans-serif', 'monospace', 'cursive', 'fantasy', 'system-ui'];
  if (genericFonts.includes(primaryFont.toLowerCase())) {
    return null;
  }

  return primaryFont;
};

/**
 * List of common system/default fonts that don't need to be loaded from Google Fonts
 */
const SYSTEM_FONTS = [
  // Generic font families
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
  'ui-serif',
  'ui-sans-serif',
  'ui-monospace',
  'ui-rounded',
  // Windows fonts
  'arial',
  'arial black',
  'calibri',
  'cambria',
  'candara',
  'comic sans ms',
  'consolas',
  'constantia',
  'corbel',
  'courier new',
  'georgia',
  'impact',
  'lucida console',
  'lucida sans unicode',
  'palatino linotype',
  'segoe ui',
  'tahoma',
  'times new roman',
  'trebuchet ms',
  'verdana',
  // macOS fonts
  'american typewriter',
  'andale mono',
  'avenir',
  'avenir next',
  'baskerville',
  'big caslon',
  'bodoni 72',
  'bradley hand',
  'brush script mt',
  'chalkboard',
  'chalkduster',
  'charter',
  'cochin',
  'copperplate',
  'courier',
  'didot',
  'futura',
  'geneva',
  'gill sans',
  'helvetica',
  'helvetica neue',
  'herculanum',
  'hoefler text',
  'lucida grande',
  'luminari',
  'marker felt',
  'menlo',
  'monaco',
  'noteworthy',
  'optima',
  'palatino',
  'papyrus',
  'phosphate',
  'rockwell',
  'savoye let',
  'sf pro',
  'sf pro display',
  'sf pro text',
  'sf mono',
  'skia',
  'snell roundhand',
  'times',
  'trattatello',
  'zapfino',
  // Linux fonts
  'dejavu sans',
  'dejavu serif',
  'dejavu sans mono',
  'liberation sans',
  'liberation serif',
  'liberation mono',
  'ubuntu',
  'ubuntu mono',
  'droid sans',
  'droid serif',
  'droid sans mono',
  'noto sans',
  'noto serif',
  'noto mono',
  'freesans',
  'freeserif',
  'freemono',
];

/**
 * Checks if a font is a system/default font
 * @param {string} fontName - The font name to check
 * @returns {boolean} - Whether the font is a system font
 */
const isSystemFont = (fontName) => {
  if (!fontName) return false;
  return SYSTEM_FONTS.includes(fontName.toLowerCase());
};

/**
 * Loads a Google Font dynamically by injecting a link element
 * Returns a Promise that resolves to true if font loaded successfully, false otherwise
 * For system fonts, returns true immediately without loading from Google Fonts
 * @param {string} fontName - The font name to load
 * @returns {Promise<boolean>} - Whether the font loaded successfully
 */
const loadGoogleFont = (fontName) => {
  return new Promise((resolve) => {
    if (!fontName) {
      resolve(false);
      return;
    }

    // If it's a system font, no need to load from Google Fonts
    if (isSystemFont(fontName)) {
      resolve(true);
      return;
    }

    // Check if link already exists
    const existingLink = document.querySelector(`link[data-font="${fontName}"]`);
    if (existingLink) {
      // Wait for existing link to load
      if (existingLink.dataset.loaded === 'true') {
        resolve(true);
        return;
      }
      if (existingLink.dataset.loaded === 'false') {
        resolve(false);
        return;
      }
      // Still loading, wait for it
      existingLink.addEventListener('load', () => resolve(existingLink.dataset.loaded === 'true'));
      existingLink.addEventListener('error', () => resolve(false));
      return;
    }

    // Create and inject the link element
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@100;200;300;400;500;600;700;800;900&display=swap`;
    link.dataset.font = fontName;

    link.onload = () => {
      // Use document.fonts.load() to actually verify the font is available
      // This is more reliable than document.fonts.check()
      if (document.fonts && document.fonts.load) {
        document.fonts
          .load(`16px "${fontName}"`)
          .then((fonts) => {
            // If fonts array is empty or has no matching font, the font didn't load
            if (fonts.length > 0) {
              link.dataset.loaded = 'true';
              resolve(true);
            } else {
              link.dataset.loaded = 'false';
              resolve(false);
            }
          })
          .catch(() => {
            link.dataset.loaded = 'false';
            resolve(false);
          });
      } else {
        // Fallback for older browsers - assume success if CSS loaded
        link.dataset.loaded = 'true';
        resolve(true);
      }
    };

    link.onerror = () => {
      link.dataset.loaded = 'false';
      resolve(false);
    };

    document.head.appendChild(link);

    // Timeout fallback - if font doesn't load in 5 seconds, consider it failed
    setTimeout(() => {
      if (!link.dataset.loaded) {
        link.dataset.loaded = 'false';
        resolve(false);
      }
    }, 5000);
  });
};

/**
 * Custom hook to parse and sanitize AR viewer URL parameters
 * Returns custom text content and styling if provided via URL
 *
 * URL Parameters:
 * - font: Global font family - automatically loads from Google Fonts
 * - title: Custom title text
 * - titleColor: Title text color
 * - titleSize: Title font size
 * - titleWeight: Title font weight
 * - desc: Custom description text
 * - descColor: Description text color
 * - descSize: Description font size
 * - descWeight: Description font weight
 * - btnText: Custom button text
 * - btnTextColor: Button text color
 * - btnSize: Button font size
 * - btnWeight: Button font weight
 * - btnPadding: Button padding (e.g., 10px, 10px 20px)
 * - btnRadius: Button border radius (e.g., 4px, 50%)
 * - btnBgColor: Button background and border color
 *
 * @returns {Object} Parsed and sanitized URL parameters
 */
const useARViewerUrlParams = () => {
  // Track whether the custom font loaded successfully
  const [fontLoaded, setFontLoaded] = useState(false);

  const baseParams = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);

    // Parse global font family (applies to all: title, description, button)
    const fontFamily = sanitizeFontFamily(searchParams.get('font'));

    // Parse title parameters
    const title = sanitizeText(searchParams.get('title'));
    const titleColor = sanitizeColor(searchParams.get('titleColor'));
    const titleFontSize = sanitizeFontSize(searchParams.get('titleSize'));
    const titleFontWeight = sanitizeFontWeight(searchParams.get('titleWeight'));

    // Parse description parameters
    const description = sanitizeText(searchParams.get('desc'));
    const descriptionColor = sanitizeColor(searchParams.get('descColor'));
    const descriptionFontSize = sanitizeFontSize(searchParams.get('descSize'));
    const descriptionFontWeight = sanitizeFontWeight(searchParams.get('descWeight'));

    // Parse button parameters
    const buttonText = sanitizeText(searchParams.get('btnText'));
    const btnTextColor = sanitizeColor(searchParams.get('btnTextColor'));
    const buttonFontSize = sanitizeFontSize(searchParams.get('btnSize'));
    const buttonFontWeight = sanitizeFontWeight(searchParams.get('btnWeight'));
    const buttonPadding = sanitizePadding(searchParams.get('btnPadding'));
    const buttonRadius = sanitizeBorderRadius(searchParams.get('btnRadius'));
    const buttonBackgroundColor = sanitizeColor(searchParams.get('btnBgColor'));

    // Check if any custom content is provided
    const hasCustomContent = !!(title || description || buttonText);

    // Parse block width and QR code size
    const blockWidth = sanitizeBorderRadius(searchParams.get('blockWidth'));
    const qrCodeSize = sanitizeFontSize(searchParams.get('qrCodeSize'));

    return {
      // Content
      title: title || null,
      description: description || null,
      buttonText: buttonText || null,

      // Style values (without fontFamily - added later based on load status)
      titleColor,
      titleFontSize,
      titleFontWeight,
      descriptionColor,
      descriptionFontSize,
      descriptionFontWeight,
      btnTextColor,
      buttonFontSize,
      buttonFontWeight,
      buttonPadding,
      buttonRadius,
      buttonBackgroundColor,

      // Helper flag
      hasCustomContent,

      // Font family for loading
      fontFamily,

      // AR viewer parent block width
      blockWidth,

      // QR code size
      qrCodeSize,
    };
  }, []);

  // Load Google Font dynamically when fontFamily is provided
  useEffect(() => {
    if (baseParams.fontFamily) {
      const primaryFont = extractPrimaryFont(baseParams.fontFamily);
      loadGoogleFont(primaryFont).then((loaded) => {
        setFontLoaded(loaded);
      });
    }
  }, [baseParams.fontFamily]);

  // Build final params with styles (fontFamily only if loaded successfully)
  const params = useMemo(() => {
    const effectiveFontFamily = fontLoaded ? baseParams.fontFamily : null;

    // Build style objects
    const titleStyle = {};
    if (baseParams.titleColor) titleStyle.color = baseParams.titleColor;
    if (baseParams.titleFontSize) titleStyle.fontSize = baseParams.titleFontSize;
    if (baseParams.titleFontWeight) titleStyle.fontWeight = baseParams.titleFontWeight;
    if (effectiveFontFamily) titleStyle.fontFamily = effectiveFontFamily;

    const descriptionStyle = {};
    if (baseParams.descriptionColor) descriptionStyle.color = baseParams.descriptionColor;
    if (baseParams.descriptionFontSize) descriptionStyle.fontSize = baseParams.descriptionFontSize;
    if (baseParams.descriptionFontWeight) descriptionStyle.fontWeight = baseParams.descriptionFontWeight;
    if (effectiveFontFamily) descriptionStyle.fontFamily = effectiveFontFamily;

    const buttonStyle = {};
    if (baseParams.btnTextColor) buttonStyle.color = baseParams.btnTextColor;
    if (baseParams.buttonBackgroundColor) {
      buttonStyle.backgroundColor = baseParams.buttonBackgroundColor;
      buttonStyle.borderColor = baseParams.buttonBackgroundColor;
    }
    if (baseParams.buttonFontSize) buttonStyle.fontSize = baseParams.buttonFontSize;
    if (baseParams.buttonFontWeight) buttonStyle.fontWeight = baseParams.buttonFontWeight;
    if (effectiveFontFamily) buttonStyle.fontFamily = effectiveFontFamily;
    if (baseParams.buttonPadding) buttonStyle.padding = baseParams.buttonPadding;
    if (baseParams.buttonRadius) buttonStyle.borderRadius = baseParams.buttonRadius;

    return {
      // Content
      title: baseParams.title,
      description: baseParams.description,
      buttonText: baseParams.buttonText,

      // Styles
      titleStyle: Object.keys(titleStyle).length > 0 ? titleStyle : null,
      descriptionStyle: Object.keys(descriptionStyle).length > 0 ? descriptionStyle : null,
      buttonStyle: Object.keys(buttonStyle).length > 0 ? buttonStyle : null,

      // Helper flag
      hasCustomContent: baseParams.hasCustomContent,

      // Font family (only if loaded)
      fontFamily: effectiveFontFamily,

      // QR code size
      qrCodeSize: baseParams.qrCodeSize,

      // AR viewer parent block width
      blockWidth: baseParams.blockWidth,
    };
  }, [baseParams, fontLoaded]);

  return params;
};

/**
 * Custom hook to parse and sanitize 360 viewer URL parameters
 * Returns custom help text content and styling if provided via URL
 *
 * URL Parameters:
 * - font: Font family - automatically loads from Google Fonts or uses system font
 * - helpText: Custom help text
 * - helpTextColor: Help text color
 * - helpTextSize: Help text font size
 * - helpTextWeight: Help text font weight
 *
 * @returns {Object} Parsed and sanitized URL parameters
 */
export const use360ViewerUrlParams = () => {
  // Track whether the custom font loaded successfully
  const [fontLoaded, setFontLoaded] = useState(false);

  const baseParams = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);

    // Parse font family
    const fontFamily = sanitizeFontFamily(searchParams.get('font'));

    // Parse help text parameters
    const helpText = sanitizeText(searchParams.get('helpText'));
    const helpTextColor = sanitizeColor(searchParams.get('helpTextColor'));
    const helpTextSize = sanitizeFontSize(searchParams.get('helpTextSize'));
    const helpTextWeight = sanitizeFontWeight(searchParams.get('helpTextWeight'));

    // Check if custom text is provided
    const hasCustomContent = !!helpText;

    return {
      // Content
      helpText: helpText || null,

      // Style values (without fontFamily - added later based on load status)
      helpTextColor,
      helpTextSize,
      helpTextWeight,

      // Helper flag
      hasCustomContent,

      // Font family for loading
      fontFamily,
    };
  }, []);

  // Load Google Font dynamically when fontFamily is provided
  useEffect(() => {
    if (baseParams.fontFamily) {
      const primaryFont = extractPrimaryFont(baseParams.fontFamily);
      loadGoogleFont(primaryFont).then((loaded) => {
        setFontLoaded(loaded);
      });
    }
  }, [baseParams.fontFamily]);

  // Build final params with styles (fontFamily only if loaded successfully)
  const params = useMemo(() => {
    const effectiveFontFamily = fontLoaded ? baseParams.fontFamily : null;

    // Build style object
    const helpTextStyle = {};
    if (baseParams.helpTextColor) helpTextStyle.color = baseParams.helpTextColor;
    if (baseParams.helpTextSize) helpTextStyle.fontSize = baseParams.helpTextSize;
    if (baseParams.helpTextWeight) helpTextStyle.fontWeight = baseParams.helpTextWeight;
    if (effectiveFontFamily) helpTextStyle.fontFamily = effectiveFontFamily;

    return {
      // Content
      helpText: baseParams.helpText,

      // Style
      helpTextStyle: Object.keys(helpTextStyle).length > 0 ? helpTextStyle : null,

      // Helper flag
      hasCustomContent: baseParams.hasCustomContent,

      // Font family (only if loaded)
      fontFamily: effectiveFontFamily,
    };
  }, [baseParams, fontLoaded]);

  return params;
};

export default useARViewerUrlParams;
